/* waitDuration.js — Unified operational waiting-duration system (iter-169).
 *
 * Single source of truth for "how long has this been waiting?" copy and
 * tonal hierarchy across:
 *   • Sales Progression (Waiting on column, exchange confidence,
 *     expanded rows, chain health, involved parties).
 *   • Deal Room (Workstreams Waiting column, blockers, involved
 *     parties, right rail, activity feed references).
 *
 * Doctrine
 * --------
 * Three calm tonal bands. NEVER aggressive red, never flashing alerts,
 * never severity badges.
 *
 *     0–2 days  →  neutral   (calm slate — operational baseline)
 *     3–5 days  →  amber     (soft attention — needs gentle escalation)
 *     6+ days   →  rose      (restrained risk — but never destructive red)
 *
 * The same bands govern "silent" (no inbound reply) durations — silence
 * just frames slower. The "replied today" affordance always reads
 * emerald: a fresh response is a calm positive signal.
 *
 * Public API
 * ----------
 *   waitToneFromDays(days)     → "neutral" | "amber" | "rose" | "fresh"
 *   waitToneClass(tone)        → Tailwind classes for {text, dot, pill, ring}
 *   formatWaitLabel({days, kind, prefix})
 *                              → "Waiting 3d" / "Silent 5d" / "Replied today"
 *   daysSince(iso)             → integer days (clamped at 0)
 *   waitFromTimestamp(iso, opts) → { days, tone, label, kind } — one-stop helper
 */

const _MS_DAY = 86_400_000;

export const WAIT_TONE_THRESHOLDS = Object.freeze({
  NEUTRAL_MAX: 2,
  AMBER_MAX:   5,
});

/** Tone band from raw day count. `null`/missing → "neutral". */
export function waitToneFromDays(days) {
  if (days == null || Number.isNaN(days)) return "neutral";
  const d = Math.max(0, Math.floor(days));
  if (d <= WAIT_TONE_THRESHOLDS.NEUTRAL_MAX) return "neutral";
  if (d <= WAIT_TONE_THRESHOLDS.AMBER_MAX)   return "amber";
  return "rose";
}

/** Tailwind class map for each tone — light-surface palette (the
 *  current SalesProgression + DealRoom workstreams aesthetic). Keep
 *  hues restrained — slate/amber/rose-700 rather than -500. */
export const WAIT_TONE_CLASSES = Object.freeze({
  neutral: {
    text: "text-slate-600",
    dot:  "bg-slate-400",
    pill: "bg-slate-100 text-slate-700",
    ring: "ring-slate-200",
  },
  amber: {
    text: "text-amber-700",
    dot:  "bg-amber-500",
    pill: "bg-amber-50 text-amber-700",
    ring: "ring-amber-200",
  },
  rose: {
    text: "text-rose-700",
    dot:  "bg-rose-500",
    pill: "bg-rose-50 text-rose-700",
    ring: "ring-rose-200",
  },
  fresh: {
    text: "text-emerald-700",
    dot:  "bg-emerald-500",
    pill: "bg-emerald-50 text-emerald-700",
    ring: "ring-emerald-200",
  },
});

/** Tailwind class accessor — returns the full object for the given tone. */
export function waitToneClass(tone) {
  return WAIT_TONE_CLASSES[tone] || WAIT_TONE_CLASSES.neutral;
}

/* UK calendar-day arithmetic: compare Europe/London calendar dates, not 24h blocks,
 * so a date never flips by one around midday or across BST. Date-only ISO strings
 * ("2026-06-04") are taken as that calendar date. */
const _UK_DATE = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit",
});
const _ukDayNumber = (ms) => {
  const p = Object.fromEntries(_UK_DATE.formatToParts(new Date(ms)).map((x) => [x.type, x.value]));
  return Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day)) / _MS_DAY;
};
export function ukDayIndex(iso) {
  if (!iso) return null;
  const s = String(iso).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / _MS_DAY;
  const t = Date.parse(s);
  return Number.isNaN(t) ? null : _ukDayNumber(t);
}

/** UK calendar days from today until `iso` (negative = past). `null` if unparseable. */
export function calendarDaysUntil(iso, now = Date.now()) {
  const d = ukDayIndex(iso);
  return d === null ? null : d - _ukDayNumber(now);
}

/** UK calendar days since an ISO timestamp, clamped to zero. `null` if unparseable. */
export function daysSince(iso, now = Date.now()) {
  const d = calendarDaysUntil(iso, now);
  return d === null ? null : Math.max(0, -d);
}

/* Format a waiting label. `kind`:
 *   "waiting" → "Waiting Xd"  (default — open wait)
 *   "silent"  → "Silent Xd"   (no inbound contact)
 *   "fresh"   → "Replied today" / "Replied Xd ago"
 * `prefix` — optional operational subject inserted before the duration
 * (e.g. "Awaiting searches"). Renders as "Awaiting searches · 4d" so
 * the Sales Progression "Waiting on" column reads naturally.
 */
export function formatWaitLabel({ days, kind = "waiting", prefix }) {
  if (kind === "fresh") {
    if (days == null || days === 0) return "Replied today";
    if (days === 1) return "Replied yesterday";
    return `Replied ${days}d ago`;
  }
  const verb = kind === "silent" ? "Silent" : "Waiting";
  if (days == null) return prefix ? `${prefix} · —` : verb;
  if (days === 0) {
    // Sub-day waits stay calm: "Waiting today" reads better than "0d".
    return prefix ? `${prefix} · today` : `${verb} today`;
  }
  const dur = `${days}d`;
  return prefix ? `${prefix} · ${dur}` : `${verb} ${dur}`;
}

/** One-stop helper — pass a timestamp + options, get back days, tone,
 *  label and the inferred kind. Use this anywhere the surface needs
 *  the full operational signal in one expression.
 *
 *  opts:
 *    kind   — "waiting" | "silent" | "fresh" (auto-derived if absent)
 *    prefix — optional subject prefix (e.g. "Awaiting LPE1")
 *
 *  Auto-derivation: a `lastReplyIso` whose age ≤ 1 day flips kind to
 *  "fresh". Otherwise defaults to "waiting".
 */
export function waitFromTimestamp(iso, opts = {}) {
  const days = daysSince(iso);
  let { kind } = opts;
  if (!kind) {
    if (iso && days != null && days <= 1) kind = "fresh";
    else kind = "waiting";
  }
  const tone = kind === "fresh" ? "fresh" : waitToneFromDays(days);
  const label = formatWaitLabel({ days, kind, prefix: opts.prefix });
  return { days, tone, label, kind };
}
